import { useState } from "react";
import { MessageSquare, Lightbulb, Zap, TrendingUp, Globe, Users, Settings, Lock, MoreHorizontal } from "lucide-react";
import { C } from "../constants/colors";
import { IDEATION_MODULES, ACCELERATOR_MODULES, MOMENTUM_MODULES } from "../constants/modules";
import { JourneyBar } from "./JourneyBar";
import BrillLogo from "./BrillLogo";

export function BottomNav({ active, onSelect, onOpenSidebar, moduleMessages, hasMomentumAccess }) {
  const [overflowOpen, setOverflowOpen] = useState(false);

  const activeSection = (() => {
    if (active === "chat") return "chat";
    if (IDEATION_MODULES.includes(active)) return "academy";
    if (ACCELERATOR_MODULES.includes(active)) return "accelerator";
    if (MOMENTUM_MODULES.includes(active)) return "momentum";
    return null;
  })();

  /* THE THREE PHASE BUTTONS ALL DO THE SAME THING: open the nav on that tab.
     Her call, 2026-09-18 — "all open the nav in mobile and auto select the
     tab". They used to do three different things, none of them that: Academy
     jumped straight to the Niche Decoder, Accelerator opened the sheet and
     selected nothing, and Momentum jumped to Courses (`lesson_plans`), which
     is one tool out of eighteen and no way to choose another.

     A phase is not a destination. Ask Brill IS one, so it still navigates.

     `phase` is the nav's own key for the tab (Academy's is "ideation"), not
     this row's id — they differ for exactly that one, which is why it is
     written here rather than derived from `id`. */
  const mainItems = [
    { id: "chat",        icon: MessageSquare, label: "Ask Brill",   action: () => onSelect("chat"),          locked: false },
    { id: "academy",     icon: Lightbulb,     label: "Academy",     phase: "ideation",                       locked: false },
    { id: "accelerator", icon: Zap,           label: "Accelerator", phase: "accelerator",                    locked: false },
    { id: "momentum",    icon: TrendingUp,    label: "Momentum",    phase: "momentum",                       locked: !hasMomentumAccess },
  ];

  const overflowItems = [
    { id: "portal",    icon: Globe,     label: "Portal",    action: () => { window.open("https://portal.tevrawood.com/", "_blank"); setOverflowOpen(false); } },
    { id: "community", icon: Users,     label: "Community", action: () => { window.open("https://portal.tevrawood.com/communities/groups/business-brilliance-collab/home", "_blank"); setOverflowOpen(false); } },
    { id: "settings",  icon: Settings,  label: "Settings",  action: () => { onSelect("settings"); setOverflowOpen(false); } },
  ];

  return (
    <div style={{ flexShrink: 0, position: "relative", background: C.indigo, borderTop: `1px solid ${C.teal}44`, paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)", paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)" }}>
      <JourneyBar moduleMessages={moduleMessages} />
      {overflowOpen && (
        <div onClick={() => setOverflowOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 39 }} />
      )}
      {overflowOpen && (
        <div style={{
          position: "absolute", bottom: "100%", right: 0,
          background: C.indigo, border: `1px solid ${C.teal}44`,
          borderRadius: "10px 10px 0 0", overflow: "hidden",
          zIndex: 41, minWidth: 170,
          boxShadow: "0 -4px 20px rgba(0,0,0,0.3)",
        }}>
          {overflowItems.map(({ id, icon: Icon, label, action }) => (
            <button key={id} onClick={action} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "14px 18px", background: "none", border: "none",
              borderBottom: `1px solid ${C.teal}22`, cursor: "pointer",
              color: "#bbb", fontSize: 14,
            }}>
              <Icon size={16} color="#888" />
              {label}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", borderTop: `1px solid ${C.teal}22` }}>
        {mainItems.map(({ id, icon: Icon, label, action, phase, locked }) => {
          const isActive = activeSection === id;
          const color = locked ? "#3a3a4a" : isActive ? C.tealLight : "#555";
          return (
            /* WRAPPED, NOT PASSED. `onClick={onOpenSidebar}` handed the click
               EVENT to it as its first argument — harmless while it took none,
               a locked Momentum asking the nav for a tab called "[object
               Object]" the moment it did. A locked tool still opens its own
               tab, so she lands on the upsell for the thing she pressed. */
            <button key={id} onClick={() => (phase ? onOpenSidebar(phase) : action())} style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", gap: 3, padding: "9px 2px 7px", minHeight: 44,
              background: "none", border: "none", cursor: "pointer",
              color, position: "relative", opacity: locked ? 0.6 : 1,
            }}>
              {isActive && !locked && <div style={{ position: "absolute", top: 0, left: "20%", right: "20%", height: 2, background: C.tealLight, borderRadius: 2 }} />}
              <div style={{ position: "relative" }}>
                {/* ASK BRILL CARRIES THE BRILL MARK (her ask, 2026-10-12: a Brill
                    icon somewhere on a phone). Two official files, never a
                    recolour: the two-tone mark lit, the white one-colour mark
                    dimmed to sit with the grey tabs. */}
                {id === "chat"
                  ? <BrillLogo variant="mark" theme={isActive ? "dark" : "white"} height={19} style={isActive ? undefined : { opacity: 0.4 }} />
                  : <Icon size={17} color={color} />}
                {locked && <Lock size={8} color="#555" style={{ position: "absolute", bottom: -2, right: -4 }} />}
              </div>
              <span style={{ fontSize: 11, fontWeight: isActive ? 600 : 400 }}>{label}</span>
            </button>
          );
        })}
        <button onClick={() => setOverflowOpen(o => !o)} style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 3, padding: "9px 2px 7px", minHeight: 44,
          background: "none", border: "none", cursor: "pointer",
          color: overflowOpen ? C.tealLight : "#555", position: "relative",
        }}>
          <MoreHorizontal size={17} color={overflowOpen ? C.tealLight : "#555"} />
          <span style={{ fontSize: 11 }}>More</span>
        </button>
      </div>
    </div>
  );
}
